import React from 'react';
import BadgeCmp from 'src/components/BadgeCmp';
import { generateUnitSummary, getDateWithSuffixFormat, getFormattedDate } from 'src/utils/CommonFunctions';

import { formatLegLocation, getLegDeliveryScheduleSource, getRouteTypeLabel, isCoveredLeg, resolveLegCarrierName } from './legOverview.helpers';
import { getLegStatusBadge, getStopStateBadge } from './legOverview.status';

/** table_column_settings alias for this table — any alias is accepted by the portal backend. */
export const CP_ROUTE_OVERVIEW_TABLE_ALIAS = 'cp_order_route_overview';

/** Per-viewer "Show by Stop" preference (WALHQ keeps it on the user's orderSettings; the portal has no such key yet). */
export const ROUTE_OVERVIEW_SHOW_BY_STOP_STORAGE_KEY = 'cp_route_overview_show_by_stop';

export interface IRouteOverviewPreviewContext {
    t: (key: string, options?: Record<string, any>) => string;
    legs: any[];
}

export interface IRouteOverviewColumn {
    id: string;
    name: string;
    visible: boolean;
    displayOrder: number;
    sortable: boolean;
    previewWidth: string;
    getValue: (leg: any, context: IRouteOverviewPreviewContext) => React.ReactNode;
}

// 12-hour "hh:mm AM/PM" — same as WALHQ's formatScheduleCellDisplay. Stored values are wall-clock HH:mm[:ss].
const formatTime = (time: string): string => {
    if (!time) return '';
    const parts = time.toString().split(':');
    const hour = parseInt(parts[0], 10);
    if (isNaN(hour)) return '';
    const minute = parts[1] ? parseInt(parts[1], 10) : 0;
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;

    return `${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
};

/** Entity business hours (shipper for pickup, consignee for delivery) — fill the time for an "open" window. */
export interface IBusinessHours {
    start?: string | null;
    end?: string | null;
}

/** "Oct 1st, 2026 @ 01:00-05:00" — date (or date range) plus the window when one is set. HTML string (ordinal suffix markup). */
export const buildScheduleDisplay = (schedule: any, type: 'pickup' | 'delivery', businessHours?: IBusinessHours): string => {
    const key = type === 'pickup' ? 'Pickup' : 'Delivery';
    const startDate = schedule?.[`current${key}StartDate`] || '';
    if (!startDate) return '-';
    const endDate = schedule?.[`current${key}EndDate`] || '';
    const windowType = schedule?.[`current${key}WindowType`] || schedule?.[`initial${key}WindowType`] || 'open';
    const startStr = getDateWithSuffixFormat(getFormattedDate(startDate, 'MMM Do, YYYY', false, false));
    const isMultiDay = !!endDate && String(endDate).split('T')[0] !== String(startDate).split('T')[0];
    const dateStr = isMultiDay ? `${startStr} - ${getDateWithSuffixFormat(getFormattedDate(endDate, 'MMM Do, YYYY', false, false))}` : startStr;

    // "open" = Business Hours window: no time of its own, the entity's hours fill it in (WALHQ rule).
    if (windowType === 'open') {
        const bhStart = formatTime(businessHours?.start || '');
        const bhEnd = formatTime(businessHours?.end || '');

        return bhStart && bhEnd ? `${dateStr} @ ${bhStart} - ${bhEnd}` : dateStr;
    }
    const isWindow = windowType === 'ready_time' || windowType === 'delivery_time';
    const fmtStart = formatTime(schedule?.[`current${key}StartTime`] || '');
    const fmtEnd = formatTime(schedule?.[`current${key}EndTime`] || '');
    if (!fmtStart) return dateStr;
    if (isWindow && fmtEnd) return `${dateStr} @ ${fmtStart} - ${fmtEnd}`;

    return `${dateStr} @ ${fmtStart}`;
};

/** Date (or date range) only — the visible text of the Pickup / Delivery Time cells; the time sits behind the chevron. */
export const buildScheduleDateRange = (schedule: any, type: 'pickup' | 'delivery'): string => {
    const key = type === 'pickup' ? 'Pickup' : 'Delivery';
    const startDate = schedule?.[`current${key}StartDate`] || '';
    if (!startDate) return '-';
    const endDate = schedule?.[`current${key}EndDate`] || '';
    const startStr = getDateWithSuffixFormat(getFormattedDate(startDate, 'MMM Do, YYYY', false, false));
    const isMultiDay = !!endDate && String(endDate).split('T')[0] !== String(startDate).split('T')[0];

    return isMultiDay ? `${startStr} - ${getDateWithSuffixFormat(getFormattedDate(endDate, 'MMM Do, YYYY', false, false))}` : startStr;
};

/** True for a Business Hours ("open") window — the dropdown shows the entity's hours instead of a saved time. */
export const isBusinessHoursWindow = (schedule: any, type: 'pickup' | 'delivery'): boolean => {
    const key = type === 'pickup' ? 'Pickup' : 'Delivery';
    const windowType = schedule?.[`current${key}WindowType`] || schedule?.[`initial${key}WindowType`] || 'open';

    return windowType === 'open' && !!schedule?.[`current${key}StartDate`];
};

/**
 * True when the schedule earns a dropdown: a saved time, or a Business Hours window (WALHQ rule —
 * without either the card would only repeat the date already shown in the cell).
 */
export const hasScheduleTime = (schedule: any, type: 'pickup' | 'delivery'): boolean => {
    const key = type === 'pickup' ? 'Pickup' : 'Delivery';
    if (isBusinessHoursWindow(schedule, type)) return true;

    return !!schedule?.[`current${key}StartTime`];
};

/** Plain-text twin of buildScheduleDisplay for the column-manager preview cards. */
const scheduleText = (schedule: any, type: 'pickup' | 'delivery'): string => buildScheduleDisplay(schedule, type).replace(/<[^>]+>/g, '');

/**
 * Default columns — same ids as WALHQ's ROUTE_OVERVIEW_DEFAULT_COLUMNS so the two tables stay
 * comparable, but the portal's default view (client decision 2026-09-16) is the seven legacy
 * columns: ID, Status, Eqpt. Type, Mode, Pickup Time, Delivery Time, Description. Type, Carrier,
 * Origin and Destination ship hidden and are enabled through the column manager. Array order is
 * WALHQ's; displayOrder is what the table follows. `getValue` feeds only the manager's preview card.
 */
export const ROUTE_OVERVIEW_DEFAULT_COLUMNS: IRouteOverviewColumn[] = [
    { id: 'id', name: 'ID', visible: true, displayOrder: 1, sortable: false, previewWidth: '150px', getValue: (leg) => (leg?.legId ? `#${leg.legId}` : '-') },
    { id: 'routeType', name: 'Type', visible: false, displayOrder: 8, sortable: false, previewWidth: '110px', getValue: (leg, ctx) => getRouteTypeLabel(leg, ctx.legs, ctx.t) },
    { id: 'carrier', name: 'Carrier', visible: false, displayOrder: 9, sortable: false, previewWidth: '150px', getValue: (leg) => (isCoveredLeg(leg) ? '' : resolveLegCarrierName(leg) || '-') },
    {
        id: 'status',
        name: 'Status',
        visible: true,
        displayOrder: 2,
        sortable: false,
        previewWidth: '130px',
        getValue: (leg, ctx) => {
            const badge = isCoveredLeg(leg) ? getStopStateBadge(leg, ctx.legs, ctx.t) : getLegStatusBadge(leg);

            return badge ? (
                <BadgeCmp style="modern" mainClassName="bg-white rounded-md" type={badge.type}>
                    {badge.label}
                </BadgeCmp>
            ) : (
                '-'
            );
        },
    },
    { id: 'type', name: 'Eqpt. Type', visible: true, displayOrder: 3, sortable: false, previewWidth: '130px', getValue: (leg) => (isCoveredLeg(leg) ? '' : leg?.equipmentTypeFullName || '-') },
    { id: 'origin', name: 'Origin', visible: false, displayOrder: 10, sortable: false, previewWidth: '175px', getValue: (leg) => formatLegLocation(leg?.shipper?.shipperAddress) || '-' },
    { id: 'destination', name: 'Destination', visible: false, displayOrder: 11, sortable: false, previewWidth: '175px', getValue: (leg) => formatLegLocation(leg?.consignee?.consigneeAddress) || '-' },
    { id: 'mode', name: 'Mode', visible: true, displayOrder: 4, sortable: false, previewWidth: '100px', getValue: (_leg, ctx) => ctx.t('Common.Road') },
    { id: 'pickup_date', name: 'Pickup Time', visible: true, displayOrder: 5, sortable: false, previewWidth: '160px', getValue: (leg) => scheduleText(leg?.pickupDeliverySchedule, 'pickup') },
    {
        id: 'delivery_date',
        name: 'Delivery Time',
        visible: true,
        displayOrder: 6,
        sortable: false,
        previewWidth: '160px',
        getValue: (leg) => scheduleText(getLegDeliveryScheduleSource(leg), 'delivery'),
    },
    { id: 'description', name: 'Description', visible: true, displayOrder: 7, sortable: false, previewWidth: '140px', getValue: (leg) => generateUnitSummary(leg?.order_dimensions) || '-' },
];

// Shared per-column body-cell width classes: consumed by BOTH LegOverviewRow (real data) and
// LegOverviewLoaderRow (skeleton) so a width change is edited once and the two stay in sync.
export const LEG_OVERVIEW_COLUMN_CLASSNAME_MAP: Record<string, string> = {
    checkbox: 'w-[40px] min-w-[40px] max-w-[40px]',
    id: 'max-w-px min-w-[170px]',
    routeType: 'min-w-[110px] max-w-[8.27%]',
    carrier: 'min-w-[150px] max-w-px',
    status: 'min-w-[130px] max-w-[8.27%]',
    type: 'min-w-[130px] max-w-[8.27%]',
    origin: 'min-w-[175px] max-w-px',
    destination: 'min-w-[175px] max-w-px',
    mode: 'min-w-[100px] max-w-[8.27%]',
    pickup_date: 'min-w-[160px] max-w-[10%]',
    delivery_date: 'min-w-[160px] max-w-[10%]',
    description: 'min-w-[140px] max-w-px',
};

export const ROUTE_OVERVIEW_COLUMN_TRANSLATION_MAP: Record<string, string> = {
    id: 'Orders.Route_Overview_Table.ID',
    routeType: 'Orders.Route_Overview_Table.Type',
    carrier: 'Orders.Route_Overview_Table.Carrier',
    status: 'Orders.Route_Overview_Table.Status',
    type: 'Orders.Route_Overview_Table.Eqpt_Type',
    origin: 'Orders.Route_Overview_Table.Origin',
    destination: 'Orders.Route_Overview_Table.Destination',
    mode: 'Orders.Route_Overview_Table.Mode',
    pickup_date: 'Orders.Route_Overview_Table.Pickup_Time',
    delivery_date: 'Orders.Route_Overview_Table.Delivery_Time',
    description: 'Orders.Route_Overview_Table.Description',
};
